import axios from "axios";
import { getToken, saveToken, saveRefreshToken, getRefreshToken, clearAllTokens } from "@/utils/storage";

// Prod API lives on Railway. If VITE_API_BASE_URL isn't wired up at build
// time (e.g. Vercel env var missing or overridden to empty), fall back to
// the known prod host so relative URLs don't land on Vercel's static
// hosting — that's what causes the 405 on POST /admin/login.
//
// Also normalize: Vercel env vars have historically been set without the
// https:// prefix, which makes axios treat the value as a relative path
// and produce URLs like <vercel>/ballknowledgeapis.../admin/login. Force
// a protocol on and drop any trailing slash so axios resolves absolute.
function normalizeBaseUrl(raw: string | undefined): string {
  const fallback = "https://api.fansballknowledge.com";
  const value = (raw ?? "").trim();
  if (!value) return fallback;
  const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  return withProtocol.replace(/\/+$/, "");
}

const API_BASE_URL = normalizeBaseUrl(import.meta.env.VITE_API_BASE_URL);

if (!import.meta.env.VITE_API_BASE_URL) {
  // eslint-disable-next-line no-console
  console.warn(
    "[client] VITE_API_BASE_URL not set — falling back to Railway prod."
  );
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // 60s covers image uploads to Cloudinary on slow networks.
  // Most API calls resolve in <2s so the ceiling only matters on outright hangs.
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach access token to every request
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Track whether a refresh is already in progress so parallel
// 401s don't fire multiple concurrent refresh requests.
let isRefreshing = false;
let pendingQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const flushQueue = (token: string | null, error: unknown = null) => {
  pendingQueue.forEach((p) => (token ? p.resolve(token) : p.reject(error)));
  pendingQueue = [];
};

apiClient.interceptors.response.use(
  (res) => res.data,
  async (error) => {
    const original = error.config;

    // Not a 401, or already retried, or it's the refresh call itself → give up
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes("/admin/refresh")
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request until the ongoing refresh resolves
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(original));
          },
          reject,
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error("No refresh token");

      const data: any = await apiClient.post("/admin/refresh", { refreshToken });
      const newAccess: string = data.accessToken;
      const newRefresh: string = data.refreshToken;

      saveToken(newAccess);
      saveRefreshToken(newRefresh);

      apiClient.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
      flushQueue(newAccess);

      original.headers.Authorization = `Bearer ${newAccess}`;
      return apiClient(original);
    } catch (refreshError) {
      flushQueue(null, refreshError);
      clearAllTokens();
      window.location.href = "/login";
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
